import { useEffect, useMemo, useState } from 'react';
import { suppliersApi } from '@/api/suppliers.api';
import type { SelectOption } from '@/components/ui/Select';
import type { SupplierResponse } from '@/types/supplier';
import { formatSupplierLabel, formatSupplierShort } from '@/utils/entityLabels';
import { showApiError } from '@/utils/apiError';

export function useSupplierOptions(pageSize = 500) {
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    suppliersApi
      .list({ page: 0, size: pageSize, activeOnly: true })
      .then((res) => setSuppliers(res.content))
      .catch((err) => showApiError(err, 'Failed to load suppliers'))
      .finally(() => setLoading(false));
  }, [pageSize]);

  const options: SelectOption[] = useMemo(
    () => suppliers.map((s) => ({ value: String(s.id), label: formatSupplierLabel(s) })),
    [suppliers],
  );

  const byId = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);

  const getSupplier = (id: number | string | null | undefined) => {
    if (id == null || id === '') return undefined;
    return byId.get(Number(id));
  };

  const getLabel = (id: number | string | null | undefined, short = false) => {
    const row = getSupplier(id);
    if (!row) return id != null && id !== '' ? `#${id}` : '—';
    return short ? formatSupplierShort(row) : formatSupplierLabel(row);
  };

  return { suppliers, options, loading, getSupplier, getLabel, byId };
}
